import React, { PureComponent } from "react";
import { Link } from "react-router-dom";
import * as Icon from "react-feather";

import TrackerCell from "./TrackerCell";
import NativeCell from "./NativeCell";
import { buildOrderedColumns } from "./columnOrder";

/**
 * One full row in the tracker grid (anchor cell + optional native cells + user cells).
 *
 * PureComponent — only re-renders when its props change. Combined with stable parent
 * handlers, this means a single cell edit only re-renders that one row (not the
 * other 249).
 *
 * Handlers contract:
 *   - onTrackerCellChange(engagementId, fieldId, value)
 *   - onNativeCellChange(engagementId, fieldKey, value)
 */
class TrackerRow extends PureComponent {
	onTrackerCellChange = (fieldId, value) => {
		this.props.onTrackerCellChange(this.props.row.engagement_id, fieldId, value);
	};

	onNativeCellChange = (fieldKey, value) => {
		this.props.onNativeCellChange(this.props.row.engagement_id, fieldKey, value);
	};

	renderNativeCell(col) {
		const { row, flowStates, flows, teams } = this.props;
		let value;
		if (col.key === "flow_state_id") {
			value = row.native ? row.native.flow_state_id : null;
		} else if (col.key === "flow_id") {
			value = row.native ? row.native.flow_id : null;
		} else if (col.key === "team_id") {
			value = row.native ? row.native.team_id : null;
		} else if (col.key === "tax_estimate") {
			value = row.native ? !!row.native.tax_estimate : false;
		} else if (col.key === "client_id") {
			value = row.client_id;
		} else if (row.native) {
			value = row.native[col.key];
		}
		const basis = "var(--etw-n_" + col.key + ", " + col.width + "px)";
		return (
			<div
				className="et-grid__td et-grid__td--native"
				key={"nc-" + col.key}
				style={{ flex: "0 0 " + basis, width: basis }}
			>
				<NativeCell
					type={col.type}
					value={value}
					readonly={col.readonly}
					row={row}
					flowStates={flowStates}
					flows={flows}
					teams={teams}
					onChange={val => this.onNativeCellChange(col.key, val)}
				/>
			</div>
		);
	}

	renderCustomCell(f) {
		const { row, fieldsById, datasourceLabels } = this.props;
		// For dependent datasource dropdowns, surface the parent cell's selected
		// value + entity. When the parent is the Engagement datasource, the row's
		// own engagement IS the value (auto-selected), so use it directly.
		let parentValue = null;
		let parentEntityKey = null;
		if (f.datasource && f.datasource.parentFieldId && fieldsById) {
			const parentField = fieldsById[f.datasource.parentFieldId];
			parentEntityKey = parentField && parentField.datasource ? parentField.datasource.entity : null;
			parentValue = parentEntityKey === "engagement" ? row.engagement_id : row.values[f.datasource.parentFieldId];
		}
		const basis = "var(--etw-f_" + f.id + ", 220px)";
		return (
			<div className="et-grid__td" key={"fc-" + f.id} style={{ flex: "0 0 " + basis, width: basis }}>
				<TrackerCell
					field={f}
					value={row.values[f.id]}
					parentValue={parentValue}
					parentEntityKey={parentEntityKey}
					datasourceLabels={datasourceLabels ? datasourceLabels[f.id] : null}
					engagementId={row.engagement_id}
					engagementName={row.engagement_name}
					onChange={val => this.onTrackerCellChange(f.id, val)}
				/>
			</div>
		);
	}

	onToggleExpand = e => {
		e.preventDefault();
		e.stopPropagation();
		if (this.props.onToggleExpand) this.props.onToggleExpand(this.props.row.engagement_id);
	};

	render() {
		const { row, hiddenColumns, isGrouped, expanded, nativeColumns, fields, columnOrder } = this.props;

		const hidden = hiddenColumns || [];
		const ordered = buildOrderedColumns(nativeColumns, fields, columnOrder).filter(
			d => hidden.indexOf(d.colKey) === -1,
		);

		return (
			<div
				className={"et-grid__row " + (isGrouped ? "et-grid__row--grouped" : "") + (expanded ? " et-grid__row--expanded" : "")}
				data-row-key={row.engagement_id}
			>
				<div className="et-grid__td et-grid__td--anchor et-grid__td--anchor-exp">
					<button
						className={"et-row-exp " + (expanded ? "et-row-exp--open" : "")}
						onClick={this.onToggleExpand}
						title={expanded ? "Hide tasks & users" : "Show tasks & users"}
					>
						<Icon.ChevronRight size={13} />
					</button>
					<Link to={`/engagements/${row.engagement_id}`} className="et-grid__client-link">
						{!isGrouped && (
							<div className="et-grid__client-name">{row.client_name || "(No client)"}</div>
						)}
						<div className="et-grid__engagement-name">{row.engagement_name}</div>
					</Link>
				</div>

				{ordered.map(d => (d.kind === "native" ? this.renderNativeCell(d.col) : this.renderCustomCell(d.field)))}
			</div>
		);
	}
}

export default TrackerRow;
